import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ImageUpload } from "./ImageUpload";
import { Trash2 } from "lucide-react";

interface Item { id: string; image_url: string; caption: string | null; sort_order: number; visible: boolean; }

export function GalleryTab() {
  const [items, setItems] = useState<Item[]>([]);
  const [newUrl, setNewUrl] = useState("");
  const [newCaption, setNewCaption] = useState("");

  async function load() {
    const { data } = await supabase.from("gallery").select("*").order("sort_order");
    setItems((data as Item[]) || []);
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!newUrl) return toast.error("Upload an image first");
    const { error } = await supabase.from("gallery").insert({ image_url: newUrl, caption: newCaption, sort_order: items.length });
    if (error) return toast.error(error.message);
    setNewUrl(""); setNewCaption(""); load(); toast.success("Added");
  }

  async function update(id: string, patch: Partial<Item>) {
    const { error } = await supabase.from("gallery").update(patch).eq("id", id);
    if (error) toast.error(error.message); else load();
  }

  async function remove(id: string) {
    const { error } = await supabase.from("gallery").delete().eq("id", id);
    if (error) toast.error(error.message); else { load(); toast.success("Deleted"); }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl">Gallery / Work Photos</h2>
        <p className="text-sm text-muted-foreground">Upload photos of your repair work to show on the website.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="font-semibold">Add new photo</div>
        <ImageUpload value={newUrl} onChange={setNewUrl} folder="gallery" label="Photo" />
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Caption (optional)</Label>
          <Input value={newCaption} onChange={(e) => setNewCaption(e.target.value)} placeholder="e.g. Split AC service" className="mt-1.5" />
        </div>
        <Button onClick={add} size="sm">Add to gallery</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-border bg-card p-3 space-y-2">
            <img src={it.image_url} alt="" className="aspect-video w-full rounded-lg object-cover" />
            <Input
              value={it.caption || ""}
              onChange={(e) => setItems((xs) => xs.map((x) => x.id === it.id ? { ...x, caption: e.target.value } : x))}
              onBlur={(e) => update(it.id, { caption: e.target.value })}
              placeholder="Caption"
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={it.visible} onChange={(e) => update(it.id, { visible: e.target.checked })} />
                Visible
              </label>
              <Button size="sm" variant="ghost" onClick={() => remove(it.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No photos yet.</p>}
      </div>
    </div>
  );
}
